import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useRecoilValue } from 'recoil';
import { FlexColumn, Flex, FlexCenter, Loader } from '@ubeya/shared-web/components';

import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import { EmployeeCard } from 'shared/components';
import { schedulingHourlyLightView } from 'shared/atoms/scheduling';
import EmployeeTimeAvailabilityModal from 'scheduling/components/WeekView/EmployeeTimeAvailabilityModal';

const Wrapper = styled(FlexColumn)`
  background: ${({ theme }) => theme.colors.white};
  flex: 1;
  overflow: auto;
  opacity: ${({ isLoading }) => (isLoading ? 0.3 : 1)};
  ${({ addBottomMargin }) => addBottomMargin && 'margin-bottom: 80px;'}
`;

const Container = styled(FlexColumn)`
  flex: 1;
  overflow: auto;
`;

const LoaderWrapper = styled(FlexCenter)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
`;

const Row = styled(Flex)`
  ${'' /* position: relative; */}
  ${'' /* height: max-content; */}
  ${({ $withoutBottomBorder }) =>
    !$withoutBottomBorder &&
    css`
      border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
    `};

  ${'' /* ${({ isFull }) => isFull && 'flex: 1;'}; */}
`;

const Cell = styled.div`
  flex: 1;
  border-left: 1px solid ${({ theme }) => theme.colors.grey2};
`;

const HeaderCell = styled(Cell)`
  background-color: ${({ theme }) => theme.colors.grey1} !important;
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: ${({ columns }) => `250px repeat(${columns}, 1fr);`}
  ${'' /* grid-template-rows: max-content; */}
  ${'' /* position: relative; */}
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
  ${'' /* ${({ isFull }) => isFull && 'flex: 1;'}; */}
`;

const Seperator = styled.div`
  background-color: ${({ theme }) => theme.colors.grey3};
  width: 100%;
  height: 1px;
`;

const LeftCell = styled(Cell)`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-basis: 250px;
  width: 100%;
  padding: 5px 10px;
`;

const FirstLeftCell = styled(Cell)`
  flex: none;
  flex-basis: 250px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
`;

const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${({ theme }) => theme.colors.white};
`;

const Footer = styled(Header)`
  z-index: 1;
`;

const HourlyWeeklyView = ({
  isLoading,
  data = [],
  weeklyView,
  RowRenderer,
  DayRenderer,
  HeaderRenderer,
  offset,
  t,
  extraColumns = [],
  scrollRef,
  onAmountsCellClick,
  firstColumnHeaderRenderer,
  firstColumnFooterRenderer,
  onAddOpenShift,
  bookedOutsideAvailability,
  showLeftRow = true,
  employeesMap = {},
  mappedPositions,
  mappedLocations,
  mappedClientBranches,
  accountId,
  mappedAdmins,
  isUbeyaAdmin,
  employeesData = {},
  columns,
  refetchProjects,
  groupByStatus,
  refetchTimeAvailability,
  showClientPayrollStats = true,
  headerRows = [],
  numberOfHoursBuckets,
  footerRows = [],
  addEmployeeModal,
  onCloseAddEmployeeModal,
  addBottomMargin = false
}) => {
  const [selectedEmployee, setSelectedEmployee] = useState(false);
  const [timeAvailabilityModal, setTimeAvailabilityModal] = useState(false);
  const { timeFormat, theme } = useContext(ConfigContext);
  const { mappedLabels } = useLabels();
  const lightView = useRecoilValue(schedulingHourlyLightView);

  const children = useMemo(
    () =>
      data.map((item) => {
        const { groupId, rows, ...rest } = item;

        return (
          <React.Fragment key={groupId}>
            {(rows && rows.length > 0 ? rows : [{}]).map(({ rowId }) => (
              <GridRow key={rowId || ''} columns={numberOfHoursBuckets}>
                {showLeftRow && (
                  <div>
                    {rowId !== undefined && RowRenderer && (
                      <RowRenderer
                        {...rest}
                        weeklyView={weeklyView}
                        employeesData={employeesData}
                        value={rowId}
                        employeesMap={employeesMap}
                        bookedOutsideAvailability={bookedOutsideAvailability}
                        onPress={setSelectedEmployee}
                        onClickAvailability={setTimeAvailabilityModal}
                      />
                    )}
                  </div>
                )}
                {extraColumns.map(({ CellRenderer }, index) => (
                  <Cell key={`${groupId}-${rowId}-${index}`} style={{ maxWidth: 100 }}>
                    <CellRenderer group={groupId} row={rowId} {...rest} />
                  </Cell>
                ))}
                {columns.map((column, index) => {
                  const formattedDate = column.date.format(API_DATE_FORMAT);

                  return (
                    <DayRenderer
                      lightView={lightView}
                      mappedLabels={mappedLabels}
                      {...column}
                      rowData={item}
                      index={index}
                      group={groupId}
                      timeFormat={timeFormat}
                      row={rowId}
                      offset={offset}
                      t={t}
                      bookedOutsideAvailability={bookedOutsideAvailability?.[groupId]?.[formattedDate]}
                      employeesData={employeesData}
                      employeesMap={employeesMap}
                      mappedPositions={mappedPositions}
                      mappedClientBranches={mappedClientBranches}
                      mappedLocations={mappedLocations}
                      accountId={accountId}
                      mappedAdmins={mappedAdmins}
                      isUbeyaAdmin={isUbeyaAdmin}
                      key={`${groupId}-${rowId}-${index}`}
                    />
                  );
                })}
              </GridRow>
            ))}
          </React.Fragment>
        );
      }),
    [
      data,
      numberOfHoursBuckets,
      showLeftRow,
      RowRenderer,
      weeklyView,
      employeesData,
      employeesMap,
      bookedOutsideAvailability,
      extraColumns,
      columns,
      lightView,
      mappedLabels,
      timeFormat,
      offset,
      t,
      mappedPositions,
      mappedClientBranches,
      mappedLocations,
      accountId,
      mappedAdmins,
      isUbeyaAdmin
    ]
  );

  const HoursRow = useCallback(
    (row) => {
      const cellStyle = { backgroundColor: theme.colors.white };
      const extraColumnsStyle = { maxWidth: 100, backgrounColor: theme.colors.white };

      return (
        <>
          {showLeftRow && <FirstLeftCell>{row.firstColumnHeaderRenderer}</FirstLeftCell>}

          {extraColumns.map((column, index) => (
            <HeaderCell key={index} style={extraColumnsStyle}>
              <HeaderRenderer {...column} index={index} />
            </HeaderCell>
          ))}

          {columns.map((column, index) => (
            <HeaderCell key={index} style={cellStyle}>
              <HeaderRenderer {...column} index={index} />
            </HeaderCell>
          ))}
        </>
      );
    },
    [columns, extraColumns, showLeftRow, theme.colors.white]
  );

  const refetchOnAvailabilityChange = useCallback(() => {
    // refetchProjects?.();
    refetchTimeAvailability?.();
  }, [refetchTimeAvailability]);

  return (
    <Wrapper isLoading={isLoading} addBottomMargin={addBottomMargin}>
      {isLoading && (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      )}
      <Container ref={scrollRef}>
        <>
          <Header>
            {headerRows.every((x) => x !== null) && (
              <Row isHeader>
                <HoursRow firstColumnHeaderRenderer={firstColumnHeaderRenderer?.[0]} />
              </Row>
            )}

            {headerRows.map((HeaderRow, idx) => (
              <Row key={idx} $withoutBottomBorder={idx === headerRows.length - 1 || HeaderRow === undefined}>
                {HeaderRow === null && <HoursRow firstColumnHeaderRenderer={firstColumnHeaderRenderer?.[idx]} />}
                {HeaderRow !== null && HeaderRow !== undefined && (
                  <>
                    {showLeftRow && <LeftCell>{firstColumnHeaderRenderer?.[idx]}</LeftCell>}
                    <HeaderRow
                      onAddOpenShift={onAddOpenShift}
                      mappedPositions={mappedPositions}
                      mappedLocations={mappedLocations}
                      mappedClientBranches={mappedClientBranches}
                      weekView
                      groupByStatus={groupByStatus}
                      t={t}
                      refetchProjects={refetchProjects}
                      columns={columns}
                      extraColumns={extraColumns}
                      showClientPayrollStats={showClientPayrollStats}
                      showLeftRow={showLeftRow}
                      onAmountsCellClick={onAmountsCellClick}
                      employeesData={employeesData}
                    />
                  </>
                )}
              </Row>
            ))}
            <Seperator />
          </Header>

          {children}

          <Footer>
            {footerRows.map((FooterRow, idx) => (
              <Row key={idx}>
                {FooterRow !== null && (
                  <>
                    {showLeftRow && <LeftCell>{firstColumnFooterRenderer?.[idx]}</LeftCell>}
                    <FooterRow columns={columns} />
                  </>
                )}
              </Row>
            ))}
          </Footer>
        </>
      </Container>
      {selectedEmployee && (
        <EmployeeCard
          defaultData={selectedEmployee}
          onSubmit={refetchOnAvailabilityChange}
          onClose={() => setSelectedEmployee()}
        />
      )}
      {timeAvailabilityModal && (
        <EmployeeTimeAvailabilityModal
          onClose={() => setTimeAvailabilityModal(false)}
          refetchOnAvailabilityChange={refetchOnAvailabilityChange}
          accountId={timeAvailabilityModal.accountId}
          employee={timeAvailabilityModal.employee}
          refetchTimeAvailability={refetchTimeAvailability}
        />
      )}
      {addEmployeeModal && <EmployeeCard onClose={onCloseAddEmployeeModal} />}
    </Wrapper>
  );
};

export default HourlyWeeklyView;
